import type { ApiProblem } from './types.ts'
import type { ScreenId } from './contracts.ts'
import { reportNativeError, reportNativeScreen } from './bridge.ts'

let accessToken: string | null = null
let apiBaseUrl = ''

export class ApiFailure extends Error {
  constructor(readonly status: number, readonly problem: ApiProblem) {
    super(problem.message)
  }
}

export function setAccessToken(value: string | null): void { accessToken = value }

export function setApiBaseUrl(value: string): void {
  const url = new URL(value, window.location.origin)
  if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('unsupported API scheme')
  apiBaseUrl = url.origin === window.location.origin ? '' : url.origin
}

/** An absolute URL for a full-page navigation, which cannot go through `api`. */
export function apiUrl(path: string): string { return `${apiBaseUrl}${path}` }

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers)
  if (options.body && !headers.has('content-type')) headers.set('content-type', 'application/json')
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)
  let response: Response
  try { response = await fetch(`${apiBaseUrl}${path}`, { ...options, headers }) } catch {
    throw new ApiFailure(0, { code: 'offline', message: 'offline', requestId: '', field: null, retryAfterSeconds: null })
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => ({ code: 'server_error', message: 'request failed', requestId: '', field: null, retryAfterSeconds: null })) as ApiProblem
    if (response.status === 401 && !path.startsWith('/v1/auth/')) {
      accessToken = null
      window.dispatchEvent(new CustomEvent('bellhook:session-expired'))
    }
    throw new ApiFailure(response.status, problem)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function restoreSession(): Promise<{ accessToken: string; account: import('./types.ts').Account } | null> {
  try {
    if (accessToken) return { accessToken, account: await api<import('./types.ts').Account>('/v1/account') }
    const session = await api<{ accessToken: string; account: import('./types.ts').Account }>('/v1/auth/refresh', { method: 'POST', body: '{}' })
    setAccessToken(session.accessToken)
    return session
  } catch { return null }
}

export async function reportScreen(screen: ScreenId, errorCode?: string): Promise<void> {
  if (errorCode) reportNativeError(screen, errorCode)
  else reportNativeScreen(screen)
  await fetch(`${apiBaseUrl}/__tk/report`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify(errorCode ? { type: 'error', screen, code: errorCode } : { type: 'screen', screen }),
  }).catch(() => undefined)
}
